import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRange, venuesText } from '../../lib/contextSummary';
import { Icon } from '../shared/Icon';
import type { ThreadMeta } from './threadTypes';

/** A chat's context in its list entry: the venues and period it counts. */
function contextMeta(thread: ThreadMeta): string | undefined {
  const vc = thread.context?.viewContext;
  if (!vc) return thread.scopeLabel;
  const period = vc.uses?.period && vc.period?.start ? formatRange(vc.period.start, vc.period.end) : null;
  return [venuesText(vc), period].filter(Boolean).join(' · ');
}

function when(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}

/** Past chats, newest first, as a dropdown in both sizes. */
export function ChatList({
  threads,
  activeId,
  variant,
  onOpen,
  onNew,
  onDelete,
}: {
  threads: ThreadMeta[];
  activeId: string | null;
  variant: 'menu';
  onOpen: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}) {
  const { t, i18n } = useTranslation('assistant');
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (!confirmId) return;
    const id = window.setTimeout(() => setConfirmId(null), 4000);
    return () => window.clearTimeout(id);
  }, [confirmId]);

  return (
    <nav className={`maitre-chats is-${variant}`} aria-label={t('maitre.chats')}>
      <div className="maitre-chats-head">
        <span className="maitre-chats-title">{t('maitre.chats')}</span>
        <button type="button" className="maitre-chats-new" onClick={onNew}>
          <Icon name="plus" size={13} />
          {t('ask.newChat')}
        </button>
      </div>
      {threads.length === 0 ? (
        <p className="maitre-chats-empty">{t('maitre.noChats')}</p>
      ) : (
        <ul className="maitre-chats-list">
          {threads.map(thread => (
            <li key={thread.id} className={`maitre-chats-item${thread.id === activeId ? ' is-on' : ''}`}>
              <button
                type="button"
                className="maitre-chats-open"
                onClick={() => onOpen(thread.id)}
                aria-current={thread.id === activeId ? 'true' : undefined}
              >
                <span className="maitre-chats-name">
                  {thread.flow === 'north-star' ? <Icon name="star" size={12} /> : null}
                  {thread.title}
                </span>
                <span className="maitre-chats-meta">
                  {[when(thread.updatedAt, i18n.language), contextMeta(thread)].filter(Boolean).join(' · ')}
                </span>
              </button>
              <button
                type="button"
                className={`maitre-chats-delete${confirmId === thread.id ? ' is-confirm' : ''}`}
                onClick={() => {
                  if (confirmId !== thread.id) {
                    setConfirmId(thread.id);
                    return;
                  }
                  setConfirmId(null);
                  onDelete(thread.id);
                }}
                aria-label={
                  confirmId === thread.id
                    ? t('maitre.confirmDeleteChat')
                    : t('maitre.deleteChat', { title: thread.title })
                }
                title={confirmId === thread.id ? t('maitre.confirmDeleteChat') : t('maitre.deleteChatShort')}
              >
                {confirmId === thread.id ? t('maitre.confirmDeleteChat') : <Icon name="trash" size={13} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
